import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Loader2, Search, FileSpreadsheet } from "lucide-react";
import { format } from "date-fns";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

const USDollar = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const DEBOUNCE_MS = 450;
const ROWS_PER_PAGE = 10;

const formatBankAccount = (bankAccount) => {
  if (bankAccount == null) return "-";
  if (typeof bankAccount === "string" || typeof bankAccount === "number") {
    return String(bankAccount);
  }
  if (typeof bankAccount === "object") {
    const account = bankAccount.account ? String(bankAccount.account) : "";
    const routing = bankAccount.routing_number ? String(bankAccount.routing_number) : "";
    if (account && routing) return `${account} (${routing})`;
    if (account) return account;
    if (routing) return routing;
  }
  return "-";
};

const bankAccountSearchText = (bankAccount) => {
  if (bankAccount == null) return "";
  if (typeof bankAccount === "string" || typeof bankAccount === "number") {
    return String(bankAccount);
  }
  if (typeof bankAccount === "object") {
    return [bankAccount.account, bankAccount.routing_number]
      .filter(Boolean)
      .map((v) => String(v))
      .join(" ");
  }
  return "";
};

export default function DREModal({ isOpen, onClose, modalData }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [showDiscounts, setShowDiscounts] = useState(false);

  const tableContainerRef = useRef(null);
  const lastParamsRef = useRef(null);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm("");
      setDebouncedTerm("");
      setShowDiscounts(false);
      fetchTransactions();
    } else {
      setTransactionData(null);
      setError(null);
      setLoading(false);
      setShowDiscounts(false);
      lastParamsRef.current = null;
      fetchRequestIdRef.current += 1;
    }
  }, [isOpen, modalData?.accountLabel, modalData?.period, modalData?.type, modalData?.dateType, modalData?.dreConfigId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedTerm]);

  useEffect(() => {
    if (isOpen && (modalData?.type === "deduction" || modalData?.type === "discounts_revenue")) {
      setCurrentPage(1);
      fetchTransactions();
    }
  }, [showDiscounts]);

  const buildQueryParams = () => {
    if (!modalData?.period) return null;
    const params = { dre_config_id: modalData.dreConfigId, period: modalData.period };
    if (modalData?.dateType) params.date_type = modalData.dateType;
    if (modalData?.type) {
      if (modalData.type === "deduction") {
        params.type = showDiscounts ? "deductions_discounts" : "deduction";
      } else if (modalData.type === "discounts_revenue") {
        params.type = "discounts_revenue";
      } else {
        params.type = modalData.type;
      }
    } else {
      const codeMatch = modalData?.accountLabel?.match(/^([\d.]+)\s/);
      const accountCode = codeMatch ? codeMatch[1] : null;
      if (!accountCode) return "NO_CODE";
      params.code = accountCode;
    }
    return params;
  };

  const fetchTransactions = async () => {
    if (!modalData?.accountLabel && !modalData?.type) return;
    const params = buildQueryParams();
    if (!params || params === "NO_CODE") {
      setError("No account code found in label");
      return;
    }
    const key = JSON.stringify(params);
    if (lastParamsRef.current === key && transactionData) return;
    lastParamsRef.current = key;

    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    setError(null);
    setTransactionData(null);
    try {
      const { data } = await api.get("/dreconfigs/transactions-by-account", { params });
      if (requestId !== fetchRequestIdRef.current) return;
      setTransactionData(data);
      setError(null);
      setCurrentPage(1);
    } catch {
      if (requestId !== fetchRequestIdRef.current) return;
      setError("Failed to load transaction details");
      setTransactionData(null);
    } finally {
      if (requestId !== fetchRequestIdRef.current) return;
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    const params = buildQueryParams();
    if (!params || params === "NO_CODE") {
      toast("parametros inválidos para exportação", { type: "error", autoClose: 2500 });
      return;
    }
    setExportingExcel(true);
    try {
      const { data } = await api.get("/dreconfigs/transactions-dre-excel", { params });
      const name = data?.name || `dre-transactions-${params.period}.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel gerado", { type: "success", autoClose: 2500 });
    } catch (err) {
      toast(err?.response?.data?.error || "Falha ao gerar Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage((prev) => {
      if (!Number.isFinite(newPage)) return prev;
      return Math.max(1, Math.min(newPage, paginated.pages));
    });
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  };

  const handleSearchInputChange = (e) => setSearchTerm(e.target.value);
  const handleClearSearch = () => setSearchTerm("");

  const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const filteredRows = useMemo(() => {
    const rows = transactionData?.transactions || [];
    const term = norm(debouncedTerm);
    if (!term) return rows;
    return rows.filter((r) => {
      return (
        norm(r.invoice_number).includes(term) ||
        norm(r.filial_name).includes(term) ||
        norm(r.chart_code).includes(term) ||
        norm(r.chart_name).includes(term) ||
        norm(r.memo).includes(term) ||
        norm(r.issuer_name).includes(term) ||
        norm(r.issuer_email).includes(term) ||
        norm(r.paymentmethod_description).includes(term) ||
        norm(r.bank_name).includes(term) ||
        norm(bankAccountSearchText(r.bank_account)).includes(term)
      );
    });
  }, [transactionData?.transactions, debouncedTerm]);

  const paginated = useMemo(() => {
    const total = filteredRows.length;
    const pages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
    const safePage = Math.min(Math.max(1, currentPage), pages);
    const start = (safePage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return { total, pages, page: safePage, rows: filteredRows.slice(start, end) };
  }, [filteredRows, currentPage]);

  useEffect(() => {
    if (currentPage > paginated.pages && paginated.pages > 0) {
      setCurrentPage(paginated.pages);
    }
    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
  }, [paginated.pages]);

  const rowKey = (txn) => {
    if (txn && txn.id != null) return String(txn.id);
    return [
      txn?.invoice_number ?? "",
      txn?.chart_code ?? "",
      txn?.chart_name ?? "",
      txn?.memo ?? "",
      txn?.amount ?? "",
      txn?.discount ?? "",
      txn?.fee ?? "",
      txn?.issuer_email ?? ""
    ].join("|");
  };

  const sourceKey = useMemo(() => {
    return JSON.stringify({
      page: paginated.page,
      term: debouncedTerm || "",
      discounts: showDiscounts ? 1 : 0,
      period: modalData?.period || "",
      type: modalData?.type || "",
      label: modalData?.accountLabel || "",
      total: paginated.total
    });
  }, [paginated.page, debouncedTerm, showDiscounts, modalData?.period, modalData?.type, modalData?.accountLabel, paginated.total]);

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      if (typeof d === "string" && d.length === 10 && d.includes("-")) {
        const [y, m, day] = d.split("-");
        return `${m}/${day}/${y}`;
      }
      return format(new Date(d), "MM/dd/yyyy");
    } catch {
      return d;
    }
  };

  if (!isOpen) return null;

  const numberOrNull = (value) => {
    if (value == null) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const computedSettlementFromRows = (transactionData?.transactions || []).reduce((sum, txn) => {
    const amount = numberOrNull(txn?.settlement_amount);
    return sum + (amount ?? 0);
  }, 0);

  const totalSettlementAmount = [
    transactionData?.summary?.total_settlement_amount,
    transactionData?.summary?.settlement_amount_total,
    transactionData?.summary?.totalSettlementAmount,
    transactionData?.total_settlement_amount,
    transactionData?.totalSettlementAmount,
  ]
    .map(numberOrNull)
    .find((v) => v != null) ?? computedSettlementFromRows;

  const totalRowsUI = paginated.total;
  const totalPages = paginated.pages;
  const currentPageSafe = paginated.page;
  const startRecord = totalRowsUI ? (currentPageSafe - 1) * ROWS_PER_PAGE + 1 : 0;
  const endRecord = totalRowsUI ? Math.min(currentPageSafe * ROWS_PER_PAGE, totalRowsUI) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.75)" }}>
      <div className={`bg-white rounded-lg shadow-xl ${isMobile ? "w-full h-full" : "w-full max-w-7xl max-h-[90vh]"} flex flex-col overflow-hidden`}>
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>IS Details - {modalData?.accountLabel || "Account"}</h2>
            <p className="text-sm text-gray-600 mt-1">Period: {modalData?.period || "-"} • Value: {modalData?.value ? USDollar.format(modalData.value) : "-"}</p>
            {transactionData?.chart_of_account && (
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-semibold">Code:</span> {transactionData.chart_of_account.code} •
                <span className="font-semibold ml-2">Name:</span> {transactionData.chart_of_account.name}
              </div>
            )}
            {transactionData && (
              <div className="mt-2 flex gap-2 text-xs">
                <div className="bg-blue-50 p-2 rounded-sm flex-1 min-w-0">
                  <div className="text-gray-600">Total Amount</div>
                  <div className="font-semibold">{USDollar.format(numberOrNull(transactionData?.summary?.total_amount) || 0)}</div>
                </div>
                {numberOrNull(transactionData?.summary?.total_discount) != null && (
                  <div className="bg-red-50 p-2 rounded-sm flex-1 min-w-0">
                    <div className="text-gray-600">Total Discount</div>
                    <div className="font-semibold">{USDollar.format(numberOrNull(transactionData?.summary?.total_discount) || 0)}</div>
                  </div>
                )}
                <div className="bg-yellow-50 p-2 rounded-sm flex-1 min-w-0">
                  <div className="text-gray-600">Total Fee</div>
                  <div className="font-semibold">{USDollar.format(numberOrNull(transactionData?.summary?.total_fee) || 0)}</div>
                </div>
                <div className="p-2 rounded-sm flex-1 min-w-0" style={{ backgroundColor: "#ecfdf5" }}>
                  <div className="text-gray-600">Total Settlement Amount</div>
                  <div className="font-semibold">{USDollar.format(totalSettlementAmount || 0)}</div>
                </div>
                <div className="bg-purple-50 p-2 rounded-sm flex-1 min-w-0">
                  <div className="text-gray-600">Count</div>
                  <div className="font-semibold">{numberOrNull(transactionData?.summary?.count) ?? (transactionData?.transactions?.length || 0)}</div>
                </div>
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              {totalRowsUI > 0 && (
                <>
                  <span>
                    Showing <span className="font-semibold">{startRecord}</span> to <span className="font-semibold">{endRecord}</span> of <span className="font-semibold">{totalRowsUI}</span> rows
                  </span>
                  <span className="text-gray-400">|</span>
                  <span>Page</span>
                  <select
                    value={currentPageSafe}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    disabled={loading || totalPages <= 1}
                  >
                    {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>{page}</option>
                    ))}
                  </select>
                  <span>of {totalPages}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by invoice, branch, chart, issuer..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="w-full sm:w-96 pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  {searchTerm && (
                    <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>

              {modalData?.type === "deduction" && (
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showDiscounts}
                    onChange={(e) => setShowDiscounts(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Discounts</span>
                </label>
              )}

              <button
                onClick={handleExportExcel}
                disabled={exportingExcel || loading || !modalData?.period}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {exportingExcel ? <Loader2 className="animate-spin" size={18} /> : <FileSpreadsheet size={18} />}
                <span>Excel</span>
              </button>
            </div>
          </div>
        </div>

        <div ref={tableContainerRef} className="flex-1 overflow-auto min-h-0">
          <table className="w-full min-w-[1600px] border-collapse">
            <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
              <tr className="border-b-2 border-gray-300">
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Invoice</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Branch</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Code</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Name</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Memo</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-700">Discount</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-700">Fee</th>
                <th className="text-right p-3 text-sm font-semibold text-gray-700">Settlement Amount</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Status</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Issuer</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Payment Method</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Bank Name</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Bank Account</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Due Date</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Accrual Date</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Settlement Date</th>
              </tr>
            </thead>
            <tbody key={sourceKey}>
              {paginated.rows.map((txn, idx) => (
                <tr
                  key={rowKey(txn)}
                  className={`border-b transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-50`}
                >
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.invoice_number ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.filial_name ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 font-mono text-xs whitespace-nowrap">{txn.chart_code ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.chart_name ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 max-w-xs truncate" title={txn.memo}>{txn.memo ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.amount != null ? USDollar.format(txn.amount) : "-"}</td>
                  <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.discount != null ? USDollar.format(txn.discount) : "-"}</td>
                  <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.fee != null ? USDollar.format(txn.fee) : "-"}</td>
                  <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.settlement_amount != null ? USDollar.format(txn.settlement_amount) : "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.status ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.issuer_name ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.paymentmethod_description ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.bank_name ?? "-"}</td>
                  <td className="p-3 text-sm text-gray-700 font-mono text-xs whitespace-nowrap">{formatBankAccount(txn.bank_account)}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(txn.due_date)}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(txn.accrual_date)}</td>
                  <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(txn.settlement_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalRowsUI > 0 && (
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 flex-shrink-0">
            <div className="text-sm text-gray-600">Page {currentPageSafe} of {totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPageSafe - 1)}
                disabled={currentPageSafe === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPageSafe} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPageSafe + 1)}
                disabled={currentPageSafe === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
